import { c as _c } from "react/compiler-runtime";
import { useWorkosAuthEnabled } from "@/lib/workos-auth";
import { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef, useSyncExternalStore } from "react";
import { useDashboardRouter as useRouter } from "@/hooks/useDashboardRouter";
import { useApi } from "@/hooks/useApi";
import { useSectionTiming } from "@/hooks/useSectionTiming";
import { useQuery, useQueryClient } from "react-query";
import { fetchOrgs, GithubSsoReauthorizationRequiredException } from "@/hooks/useConnectedGithubOrgs";
import type { AxiosError, AxiosInstance } from "axios";
import SignedOut from "./SignedOut";
import { SidebarLayoutSkeleton } from "./SidebarLayout";
import { BlacksmithLogomarkAnimationSVG } from "./BlacksmithLogoSVG";
import { AlertDialog, AlertDialogAction, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@repo/blacksmith-ui/alert-dialog";
import { BlackGitHubIcon, WhiteGitHubIcon } from "./GitHubIcon";
import { useTheme } from "next-themes";
import * as z from "zod";
import { emitGithubFirstSignupGtmEvent, readSignupConversionId, shouldEmitGithubFirstSignupGtm, stripGithubFirstSignupQuery } from "@/lib/githubAuthGtm";
import { clearOauthSuccessAppLoadGtmSessionFlag, emitOauthSuccessAppLoadGtmOnce } from "@/lib/oauthSuccessAppLoadGtm";
import { clearRdtCid } from "@/lib/appFirstTouchAttribution";
import { clearCodesmithLayoutCache } from "./sidebar/useCodesmithEnabledCache";
import { isCodesmithExtensionEmbed } from "@/lib/codesmithExtensionEmbed";
const userSchema = z.object({
  id: z.number(),
  is_superuser: z.boolean(),
  name: z.string(),
  username: z.string(),
  email: z.string(),
  avatar_url: z.string(),
  active_org_name: z.string().nullable().optional(),
  heard_from: z.string().nullable().optional(),
  first_name: z.string().nullable().optional(),
  session_count: z.number().int().nonnegative().nullable().optional(),
  // Whether this user should see in-progress/queued statuses in runs analytics.
  // Computed server-side as (rollout enabled || is_superuser). Optional to
  // tolerate older cached responses during deploys.
  show_live_statuses: z.boolean().optional(),
  control_plane_static_ips: z.array(z.string()).optional(),
  codesmith_ack_preview_enabled: z.boolean().nullable().optional(),
  codesmith_canvas_enabled: z.boolean().nullable().optional(),
  codesmith_canvas_animation_enabled: z.boolean().nullable().optional()
});
export type User = z.infer<typeof userSchema>;
interface UserContext {
  user: User | undefined;
  logout: () => void;
  setLoginModalOpen: (open: boolean) => void;
}
export const AuthContext = createContext<UserContext>({
  user: undefined,
  logout: () => {},
  // NB: Some other components can use this to effectively force the
  // user to refresh their session.
  setLoginModalOpen: () => {}
});
export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
async function fetchUser(api: AxiosInstance) {
  return api.get(`${process.env.NEXT_PUBLIC_API_URL}/api/user`);
}

// useLoginUser is a special hook meant to be used in the AuthProvider component.
// It intentionally does not use suspense, and instead returns a loading state and
// error. The AuthProvider component uses this hook to determine whether the user
// is logged in or not. If the user is not logged in, we show the SignedOut component.
export function useLoginUser() {
  const $ = _c(9);
  const api = useApi();
  let t0;
  if ($[0] !== api) {
    t0 = async () => {
      const response = await fetchUser(api);
      return response.data;
    };
    $[0] = api;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      suspense: false,
      retry: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      useErrorBoundary: false
    };
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const {
    data,
    isLoading,
    error,
    isError,
    status
  } = useQuery("user", t0, t1);
  let t2;
  if ($[3] !== data || $[4] !== error || $[5] !== isError || $[6] !== isLoading || $[7] !== status) {
    t2 = {
      user: data,
      isLoading,
      error,
      isError,
      status
    };
    $[3] = data;
    $[4] = error;
    $[5] = isError;
    $[6] = isLoading;
    $[7] = status;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  return t2;
}
export interface SessionExpirationModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
}
export function SessionExpirationModal(t0) {
  const $ = _c(13);
  const {
    open
  } = t0;
  const {
    workosAuthEnabled,
    isLoading: isFlagLoading
  } = useWorkosAuthEnabled();
  const {
    resolvedTheme
  } = useTheme();
  const router = useRouter();
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <AlertDialogHeader><AlertDialogTitle>Your session has expired. Please log in again.</AlertDialogTitle><AlertDialogDescription>You will be redirected to the login page.</AlertDialogDescription></AlertDialogHeader>;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== router.query.embed || $[2] !== workosAuthEnabled) {
    t2 = () => {
      const redirectUrl = encodeURIComponent(window.location.href);
      const loginUrl = `${process.env.NEXT_PUBLIC_API_URL}/login/${workosAuthEnabled ? "workos" : "github"}?redirect=${redirectUrl}`;
      if (isCodesmithExtensionEmbed(router.query.embed)) {
        window.open(loginUrl, "_blank");
        return;
      }
      window.location.href = loginUrl;
    };
    $[1] = router.query.embed;
    $[2] = workosAuthEnabled;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== resolvedTheme) {
    t3 = resolvedTheme === "dark" ? <BlackGitHubIcon /> : <WhiteGitHubIcon />;
    $[4] = resolvedTheme;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== isFlagLoading || $[7] !== t2 || $[8] !== t3) {
    t4 = <AlertDialogContent>{t1}<AlertDialogFooter><AlertDialogAction disabled={isFlagLoading} className="flex gap-2" variant="default" onClick={t2}>{t3}Login</AlertDialogAction></AlertDialogFooter></AlertDialogContent>;
    $[6] = isFlagLoading;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== open || $[11] !== t4) {
    t5 = <AlertDialog open={open}>{t4}</AlertDialog>;
    $[10] = open;
    $[11] = t4;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  return t5;
}
const AUTH_BYPASS_PATHS = ["/oauth/authorize", "/cli/auth"];

// Pages that skip the login card for logged-out visitors and go straight to
// GitHub OAuth; the login redirect returns them to the same URL.
const AUTO_LOGIN_PATHS = ["/autofix-settings", "/[org]/autofix-settings"];
const AUTH_HAD_SESSION_KEY = "auth-had-session";
function getAuthHadSession(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return window.sessionStorage.getItem(AUTH_HAD_SESSION_KEY) === "1";
  } catch {
    return false;
  }
}
const getServerAuthHadSession = () => false;
const subscribeToNothing = () => () => {};
export function AuthProvider(t0) {
  const $ = _c(42);
  const {
    children
  } = t0;
  const router = useRouter();
  const api = useApi();
  const queryClient = useQueryClient();
  const firstSignupGtmHandledRef = useRef(false);
  const [loginModalOpen, setLoginModalOpen] = useState(false);
  const logout = _temp;
  const {
    user,
    isLoading,
    isError,
    error,
    status
  } = useLoginUser();
  useSectionTiming("auth", status);
  let t1;
  let t2;
  if ($[0] !== api || $[1] !== queryClient) {
    t1 = () => {
      const doPrefetch = () => {
        queryClient.prefetchQuery("connectedGithubOrgs", () => fetchOrgs(api), {
          staleTime: Number.POSITIVE_INFINITY
        });
      };
      if (typeof window !== "undefined" && "requestIdleCallback" in window) {
        (window as Window).requestIdleCallback(doPrefetch, {
          timeout: 200
        });
      } else {
        setTimeout(doPrefetch, 10);
      }
    };
    t2 = [api, queryClient];
    $[0] = api;
    $[1] = queryClient;
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  useEffect(t1, t2);
  let t3;
  let t4;
  if ($[4] !== router) {
    t3 = () => {
      if (!router.isReady || firstSignupGtmHandledRef.current) {
        return;
      }
      if (!shouldEmitGithubFirstSignupGtm(router.query)) {
        return;
      }
      firstSignupGtmHandledRef.current = true;
      const conversionId = readSignupConversionId(router.query);
      emitGithubFirstSignupGtmEvent(conversionId);
      clearRdtCid();
      const nextQuery = stripGithubFirstSignupQuery(router.query);
      router.replace({
        pathname: router.pathname,
        query: nextQuery
      }, undefined, {
        shallow: true
      });
    };
    t4 = [router];
    $[4] = router;
    $[5] = t3;
    $[6] = t4;
  } else {
    t3 = $[5];
    t4 = $[6];
  }
  useEffect(t3, t4);
  const isUnauthorized = isError && error?.response && error?.response.status === 401;
  let t5;
  if ($[7] !== router.pathname) {
    t5 = AUTH_BYPASS_PATHS.some(path => router.pathname.startsWith(path));
    $[7] = router.pathname;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const shouldBypassAuth = t5;
  let t6;
  let t7;
  if ($[9] !== router.isReady || $[10] !== shouldBypassAuth || $[11] !== user) {
    t6 = () => {
      if (!router.isReady || !user || shouldBypassAuth) {
        return;
      }
      emitOauthSuccessAppLoadGtmOnce();
    };
    t7 = [router.isReady, user, shouldBypassAuth];
    $[9] = router.isReady;
    $[10] = shouldBypassAuth;
    $[11] = user;
    $[12] = t6;
    $[13] = t7;
  } else {
    t6 = $[12];
    t7 = $[13];
  }
  useEffect(t6, t7);
  const useSkeletonForLoad = useSyncExternalStore(subscribeToNothing, getAuthHadSession, getServerAuthHadSession);
  const firstAttemptFinished = user !== undefined;
  const sessionExpired = !isLoading && isUnauthorized === true && firstAttemptFinished;
  let t8;
  let t9;
  if ($[14] !== sessionExpired) {
    t8 = () => {
      if (sessionExpired) {
        clearOauthSuccessAppLoadGtmSessionFlag();
      }
    };
    t9 = [sessionExpired];
    $[14] = sessionExpired;
    $[15] = t8;
    $[16] = t9;
  } else {
    t8 = $[15];
    t9 = $[16];
  }
  useEffect(t8, t9);
  let t10;
  let t11;
  if ($[17] !== firstAttemptFinished) {
    t10 = () => {
      if (!firstAttemptFinished) {
        return;
      }
      try {
        window.sessionStorage.setItem(AUTH_HAD_SESSION_KEY, "1");
      } catch {}
    };
    t11 = [firstAttemptFinished];
    $[17] = firstAttemptFinished;
    $[18] = t10;
    $[19] = t11;
  } else {
    t10 = $[18];
    t11 = $[19];
  }
  useEffect(t10, t11);
  let t12;
  let t13;
  if ($[20] !== firstAttemptFinished || $[21] !== isUnauthorized || $[22] !== shouldBypassAuth) {
    t12 = () => {
      if (isUnauthorized && !firstAttemptFinished && !shouldBypassAuth) {
        try {
          window.sessionStorage.removeItem(AUTH_HAD_SESSION_KEY);
          clearOauthSuccessAppLoadGtmSessionFlag();
        } catch {}
      }
    };
    t13 = [isUnauthorized, firstAttemptFinished, shouldBypassAuth];
    $[20] = firstAttemptFinished;
    $[21] = isUnauthorized;
    $[22] = shouldBypassAuth;
    $[23] = t12;
    $[24] = t13;
  } else {
    t12 = $[23];
    t13 = $[24];
  }
  useEffect(t12, t13);
  let t14;
  if ($[25] !== api) {
    t14 = () => fetchOrgs(api);
    $[25] = api;
    $[26] = t14;
  } else {
    t14 = $[26];
  }
  let t15;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = {
      enabled: false,
      staleTime: Number.POSITIVE_INFINITY
    };
    $[27] = t15;
  } else {
    t15 = $[27];
  }
  const {
    error: orgsError
  } = useQuery("connectedGithubOrgs", t14, t15);
  if (isError && !isUnauthorized) {
    throw error;
  }
  if (!shouldBypassAuth && orgsError instanceof GithubSsoReauthorizationRequiredException) {
    throw orgsError;
  }
  if (isLoading && !firstAttemptFinished) {
    if (useSkeletonForLoad) {
      let t16;
      if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
        t16 = <SidebarLayoutSkeleton />;
        $[28] = t16;
      } else {
        t16 = $[28];
      }
      return t16;
    }
    let t16;
    if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
      t16 = <div className="flex h-screen items-center justify-center"><div className="w-32 h-32"><BlacksmithLogomarkAnimationSVG /></div></div>;
      $[29] = t16;
    } else {
      t16 = $[29];
    }
    return t16;
  }
  if (isUnauthorized && !firstAttemptFinished && !shouldBypassAuth) {
    let t16;
    if ($[30] !== router.pathname) {
      t16 = AUTO_LOGIN_PATHS.includes(router.pathname);
      $[30] = router.pathname;
      $[31] = t16;
    } else {
      t16 = $[31];
    }
    let t17;
    if ($[32] !== t16) {
      t17 = <><SignedOut autoLogin={t16} /></>;
      $[32] = t16;
      $[33] = t17;
    } else {
      t17 = $[33];
    }
    return t17;
  }
  let t16;
  if ($[34] !== user) {
    t16 = {
      user,
      logout,
      setLoginModalOpen
    };
    $[34] = user;
    $[35] = t16;
  } else {
    t16 = $[35];
  }
  const t17 = loginModalOpen || sessionExpired;
  let t18;
  if ($[36] !== t17) {
    t18 = <SessionExpirationModal open={t17} setOpen={setLoginModalOpen} />;
    $[36] = t17;
    $[37] = t18;
  } else {
    t18 = $[37];
  }
  let t19;
  if ($[38] !== children || $[39] !== t16 || $[40] !== t18) {
    t19 = <AuthContext.Provider value={t16}>{children}{t18}</AuthContext.Provider>;
    $[38] = children;
    $[39] = t16;
    $[40] = t18;
    $[41] = t19;
  } else {
    t19 = $[41];
  }
  return t19;
}
function _temp() {
  clearCodesmithLayoutCache();
  window.location.href = `${process.env.NEXT_PUBLIC_API_URL}/logout`;
}